import React from 'react';
import { findWhere, mapObject, constant, contains } from 'underscore';
import { withDefaultStyles } from '@bingads-webui-react/with-default-styles';
import PropTypes from 'prop-types';
import { ListSelector } from '@bingads-webui-react/list-selector';
import { I18nContext } from '@bingads-webui-react/i18n-context';
import { withOverflowProp } from '@bingads-webui-react/with-overflow-prop';
import { Glyphicon, OverlayTrigger, Tooltip } from '@bingads-webui-react/primitive';
import { FocusLock } from '@bingads-webui-react/focus-lock';
import { FocusContext } from '@bingads-webui-campaign-react/react-focus-context';
import onClickOutside from 'react-onclickoutside';
import { getStatusDropdownStyles } from './styles';

const OverflowStatusText = withOverflowProp(({
  item, isTextOverflow, i18n, forwardedRef,
}) => (
  <OverlayTrigger
    key="top"
    placement="top"
    overlay={
      <Tooltip
        id="top"
        className={isTextOverflow ? '' : 'hide'}
      >
        {i18n.getString(item.i18nText)}
      </Tooltip>
    }
  >
    <span ref={forwardedRef} className={`${item.id} overflow-status-text`}>{i18n.getString(item.i18nText)}</span>
  </OverlayTrigger>
));

class StatusDropdown extends React.PureComponent {
  static contextType = FocusContext;

  constructor(props) {
    super(props);
    this.buttonRef = React.createRef(null);
  }

  state = {
    isOpen: false,
  };

  componentDidUpdate() {
    const { suscribeFocusElement } = this.context;
    suscribeFocusElement({ scenario: 'StatusDropdown', componentRef: this.buttonRef });
  }

  componentWillUnmount() {
    const { suscribeFocusElement } = this.context;
    suscribeFocusElement({ scenario: 'StatusDropdown', componentRef: null });
  }

  onSelect = (selectedStatus) => {
    this.toggle();
    this.props.changeStatus(selectedStatus);
  };

  get dropdownItems() {
    return [{
      id: 'active',
      leftIcon: this.props.isExperiment ? 'TestBeakerSolid' : 'RadioBullet2',
      i18nText: _TL_('Enabled'),
    }, {
      id: 'paused',
      leftIcon: this.props.isExperiment ? 'TestBeaker' : 'PauseListView12',
      i18nText: _TL_('Paused'),
    }];
  }

  get items() {
    return this.dropdownItems.concat([{
      id: 'expired',
      leftIcon: 'Remove',
      i18nText: _TL_('Expired'),
    }, {
      id: 'draft',
      leftIcon: this.props.isExperiment ? 'TestBeaker' : 'PauseListView12',
      i18nText: _TL_('Draft'),
    }, {
      id: 'BudgetPaused',
      leftIcon: this.props.isExperiment ? 'TestBeaker' : 'PauseListView12',
      i18nText: _TL_('Budget paused'),
    }, {
      id: 'BudgetUserPaused',
      leftIcon: this.props.isExperiment ? 'TestBeaker' : 'PauseListView12',
      i18nText: _TL_('Budget and user paused'),
    }, {
      id: 'Suspended',
      leftIcon: 'Remove',
      i18nText: _TL_('Suspended'),
    }]);
  }

  toggle = () => {
    this.setState(state => ({ isOpen: !state.isOpen }));
  };

  handleClickOutside = () => {
    if (this.state.isOpen) {
      this.toggle();
    }
  };

  renderItem = item => (
    <I18nContext.Consumer>
      {
        i18n => (
          <div aria-label={i18n.getString(item.i18nText)}>
            <Glyphicon
              className={`icon-status-${item.id} status-icon`}
              glyph={`ba-${item.leftIcon}`}
            />
            <OverflowStatusText i18n={i18n} item={item} />
          </div>
        )
      }
    </I18nContext.Consumer>
  );

  renderSelectedItem = (item) => {
    //  paused is the default selected icon
    const selectedItem = findWhere(this.items, { id: item.id }) || this.dropdownItems[1];
    return this.renderItem(selectedItem);
  };

  render() {
    const {
      classes,
      selectedStatus,
      'aria-label': ariaLabel,
      label,
      enableDropdownStatus,
    } = this.props;
    const enableDropdown
      = contains(['active', 'paused', ...enableDropdownStatus], selectedStatus.id);

    return (
      <div className={classes.statusDropdown}>
        <div className={`status-dropdown${this.state.isOpen ? ' open' : ''}`}>
          <button
            className="dropdown-toggle"
            ref={this.buttonRef}
            type="button"
            aria-haspopup
            aria-expanded={this.state.isOpen}
            aria-label={ariaLabel}
            onClick={this.toggle}
            disabled={!enableDropdown}
          >
            {label && <span className="entity-label">{label}</span>}
            <div>
              {this.renderSelectedItem(selectedStatus)}
              {enableDropdown && (<Glyphicon
                className="icon-ChevronDown"
                glyph="ba-ChevronDown"
              />)}
            </div>
          </button>
          <div className="dropdown-menu react-dropdown-menu">
            <FocusLock
              onExit={this.handleClickOutside}
              focusFirstItemOnMount
              isControlledFocusLock
              isFocusLock={this.state.isOpen}
            >
              <ListSelector
                renderItem={this.renderItem}
                onSelect={this.onSelect}
                data={this.dropdownItems}
                selectedItem={findWhere(this.items, { id: selectedStatus.id })}
                enableArrowKeysSupport
                ariaLabel={ariaLabel}
              />
            </FocusLock>
          </div>
        </div>
      </div>
    );
  }
}

StatusDropdown.propTypes = {
  classes: PropTypes.shape(mapObject(
    getStatusDropdownStyles({}, {}),
    constant(PropTypes.string.isRequired)
  )).isRequired,
  changeStatus: PropTypes.func.isRequired,
  selectedStatus: PropTypes.shape({
    id: PropTypes.any.isRequired,
  }).isRequired,
  isExperiment: PropTypes.bool,
  'aria-label': PropTypes.string,
  label: PropTypes.string,
  enableDropdownStatus: PropTypes.arrayOf(PropTypes.string),
};

StatusDropdown.defaultProps = {
  isExperiment: false,
  label: '',
  'aria-label': '',
  enableDropdownStatus: [],
};

export const StyledStatusDropdown =
  withDefaultStyles(onClickOutside(StatusDropdown), getStatusDropdownStyles);
